<template>
  <!-- Nothing to search along until the day has a line — unless something's already on it. -->
  <div v-if="shown" class="day-highlights" :class="{ 'day-highlights--inline': inline }" @click.stop @mousedown.stop>
    <div v-if="!inline" class="day-highlights__header">
      <span class="day-highlights__title">
        {{ $t('plan_trail.route_highlights.title') }}
        <span v-if="items.length" class="day-highlights__count">{{ items.length }}</span>
      </span>
      <button
        type="button"
        class="day-highlights__search"
        :title="$t('plan_trail.route_highlights.find_more')"
        :aria-label="$t('plan_trail.route_highlights.find_more')"
        @click="searchOpen = true"
      >
        <span class="material-icons" aria-hidden="true">search</span>
      </button>
    </div>

    <div v-for="item in folded.visible" :key="item.highlight.clientId" class="day-highlights__item">
      <button type="button" class="day-highlights__row" @click="openHighlight(item.highlight)">
        <img class="day-highlights__icon" :src="iconFor(item.highlight.type)" alt="" />
        <span class="day-highlights__name">{{ item.highlight.title }}</span>
        <small class="day-highlights__meta">{{ formatDistance(item.alongM) }}</small>
      </button>
      <button
        type="button"
        class="day-highlights__remove"
        :title="$t('plan_trail.route_highlights.remove')"
        :aria-label="$t('plan_trail.route_highlights.remove')"
        @click="removeHighlight(item.highlight)"
      >
        <span class="material-icons" aria-hidden="true">close</span>
      </button>
    </div>

    <!-- Inline, between a route's ends, the search is a row of its own. -->
    <button
      v-if="inline"
      type="button"
      class="day-highlights__add"
      :title="$t('plan_trail.route_highlights.find_more')"
      @click="searchOpen = true"
    >
      <span class="material-icons" aria-hidden="true">search</span>
      <span>{{ $t('plan_trail.route_highlights.search_title') }}</span>
    </button>

    <button v-if="folded.hiddenCount" type="button" class="day-highlights__toggle" @click="expanded = true">
      {{ $t('plan_trail.route_highlights.see_more', { count: folded.hiddenCount }) }}
    </button>
    <button v-else-if="expanded && items.length > 1" type="button" class="day-highlights__toggle" @click="expanded = false">
      {{ $t('plan_trail.route_highlights.show_less') }}
    </button>

    <HighlightSearchModal v-if="searchOpen" :day-number="dayNumber" @close="searchOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import HighlightSearchModal from './HighlightSearchModal.vue';
import { foldedList, RouteHighlight } from '../services/route-highlights';
import { highlightIconUrl } from '../utils/highlightIcon';
import { dayHasLine } from '../utils/dayHasLine';
import { endpointHighlightIds } from '../services/destination-highlights';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { i18n } from '../../../i18n';

/**
 * A day's highlights, folded to the first one, each opening its sheet and
 * showing it on the map — plus a search for more to add to the day. Under a
 * heading of its own on a day card, or `inline` between a route's Start and
 * End rows (see RouteEndpoints.vue), where they read as the stops along it.
 */

const props = withDefaults(defineProps<{ dayNumber: number; inline?: boolean }>(), { inline: false });
const store = useStore();

const expanded = ref(false);
const searchOpen = ref(false);

// The day's ends — its stop, the one before it (where it starts), the trail
// start — are already its Start and End rows, so they stay out of this list.
const endpointIds = computed(() => {
  const planTrail = store.state.planTrail ?? {};
  const stops = planTrail.stageDestinations ?? {};
  return endpointHighlightIds(
    [stops[props.dayNumber], stops[props.dayNumber - 1], planTrail.trailStartDestination],
    planTrail.hikeHighlights ?? []
  );
});
const items = computed<RouteHighlight<any>[]>(() =>
  (store.getters['planTrail/highlightsByDay']?.[props.dayNumber] ?? []).filter((item: RouteHighlight<any>) => !endpointIds.value.has(item.highlight.clientId)));
const folded = computed(() => foldedList(items.value, expanded.value));
const shown = computed(() => items.value.length > 0 || dayHasLine(store.state.planTrail?.routes, props.dayNumber));

// The marker the map shows for it (see utils/highlightIcon.ts).
function iconFor(type: string): string {
  return highlightIconUrl(type);
}

/** How far into the route it is. */
function formatDistance(metres: number): string {
  return MeasurementPrefUtility.preference().smallUnitFormatter(metres, true);
}

function openHighlight(highlight: any) {
  const map = (window as any).map;
  const coordinates = highlight.coordinates;
  if (map && coordinates) {
    map.flyTo({ center: [coordinates[0], coordinates[1]], zoom: Math.max(map.getZoom?.() ?? 0, 15), essential: true });
  }
  (window as any).showHighlightDetail?.({
    clientId: highlight.clientId,
    type: highlight.type,
    title: highlight.title,
    description: highlight.description,
    dayNumber: highlight.dayNumber,
    coordinates,
  });
}

/** Deletes it, after a word — the same one its sheet asks. */
async function removeHighlight(highlight: any) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(i18n.global.t('highlight_detail_dialog.delete_confirm'))) return;
  await store.dispatch('planTrail/deleteHikeHighlight', highlight.clientId);
}

defineExpose({ openHighlight, removeHighlight });
</script>

<style scoped>
/* Doubled up so it outranks the base rule below, which is declared later. */
.day-highlights.day-highlights--inline {
  margin: 0.05rem 0 0.05rem 0.55rem;
  padding: 0.1rem 0 0.1rem 0.45rem;
  border-top: none;
  border-left: 2px dotted rgba(255, 255, 255, 0.18);
}

.day-highlights__add {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  align-self: flex-start;
  padding: 0.1rem 0.25rem;
  border: none;
  border-radius: 6px;
  background: none;
  color: #a1a4ad;
  font-size: 0.72rem;
  cursor: pointer;
}

.day-highlights__add .material-icons {
  font-size: 14px;
}

.day-highlights__add:hover {
  color: #fddf00;
  background: rgba(255, 255, 255, 0.06);
}

.day-highlights {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  margin-top: 0.35rem;
  padding-top: 0.3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.day-highlights__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.25rem;
}

.day-highlights__title {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a1a4ad;
}

.day-highlights__count {
  padding: 0 0.3rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 0.6rem;
  letter-spacing: 0;
}

.day-highlights__search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: #a1a4ad;
  cursor: pointer;
}

.day-highlights__search .material-icons {
  font-size: 16px;
}

.day-highlights__search:hover,
.day-highlights__search:focus-visible {
  background: rgba(253, 223, 0, 0.12);
  color: #fddf00;
}

.day-highlights__item {
  display: flex;
  align-items: center;
  min-width: 0;
}

.day-highlights__row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  padding: 0.15rem 0.25rem;
  border: none;
  border-radius: 6px;
  background: none;
  color: #dcdde3;
  font-size: 0.75rem;
  text-align: left;
  cursor: pointer;
}

.day-highlights__row:hover {
  background: rgba(255, 255, 255, 0.06);
}

/* Out of the way until the row is pointed at; always there on touch. */
.day-highlights__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: #a1a4ad;
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.15s, background 0.15s, color 0.15s;
}

.day-highlights__remove .material-icons {
  font-size: 15px;
}

.day-highlights__item:hover .day-highlights__remove,
.day-highlights__item:focus-within .day-highlights__remove {
  opacity: 1;
}

.day-highlights__remove:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

@media (hover: none) {
  .day-highlights__remove {
    opacity: 1;
  }
}

/* The map's own round highlight marker (see utils/highlightIcon.ts). */
.day-highlights__icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.day-highlights__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.day-highlights__meta {
  flex-shrink: 0;
  color: #a1a4ad;
}

.day-highlights__toggle {
  align-self: flex-start;
  padding: 0.1rem 0.25rem;
  border: none;
  background: none;
  color: #fddf00;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
}

.day-highlights__toggle:hover {
  text-decoration: underline;
}
</style>
